import { PowerIcon } from '@phosphor-icons/react';
import type { MouseEvent } from 'react';

import { COMMAND_TYPE, type CommandType } from '../constants';

import styles from './device-power-button.module.scss';

const LABELS: Record<CommandType, string> = {
  [COMMAND_TYPE.TURN_ON]: 'Turn on device',
  [COMMAND_TYPE.TURN_OFF]: 'Turn off device',
};

export type DevicePowerButtonProps = {
  /** The command a click would queue. `null` renders an inert control with no action. */
  command: CommandType | null;
  disabled: boolean;
  /**
   * Whether to draw the lit colorway. Deliberately independent of `disabled`: the design lights
   * the disc when the *pump* is running, whether or not a command is already queued.
   */
  energised: boolean;
  /**
   * `md` (54px) is the listing card's disc; `lg` (60px) is the Device Details control cluster's.
   * Two sizes because the design draws two — Figma 232:355195 against 219:340859 — not because the
   * control is configurable.
   */
  size?: 'md' | 'lg';
  onQueue?: (command: CommandType) => void;
  className?: string;
};

/**
 * The circular power control (Figma node 232:355195).
 *
 * Lit it is accent1-400 with a paler rim glow; otherwise a flat neutral-200 disc. The icon is
 * never removed, only greyed (US-112439 §6 "disabled (not removed) when an action is queued").
 *
 * Click handling stops propagation because the whole card is a link to the device details page,
 * and the AC requires that clicking the icon queues the action *without* navigating (§6 "Power
 * icon click opens the action only, not the card").
 */
export function DevicePowerButton({
  command,
  disabled,
  energised,
  size = 'md',
  onQueue,
  className,
}: DevicePowerButtonProps) {
  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    event.preventDefault();
    if (!disabled && command) onQueue?.(command);
  };

  return (
    <button
      type="button"
      className={[
        styles.button,
        size === 'lg' ? styles.large : undefined,
        energised ? styles.energised : styles.muted,
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      disabled={disabled}
      aria-label={command ? LABELS[command] : 'Power control unavailable'}
      onClick={handleClick}
    >
      <PowerIcon
        size={size === 'lg' ? 25 : 22}
        weight="regular"
        aria-hidden
        className={styles.icon}
      />
    </button>
  );
}
